// Draws the "Our Year in Restaurants" card to a 1080×1350 PNG (Instagram portrait), in the app's palette.
// Emoji are drawn with the phone's system emoji font.
import { cuisineLabel } from './categories.js';
import { formatLocalDate } from './date.js';

const W = 1080;
const H = 1350;
const EMOJI_FONT = '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji"';
const SANS = `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif, ${EMOJI_FONT}`;

export const RECAP_GRADIENTS = {
    classic: ['#fffbeb', '#fff7ed', '#fef2f2'],
    doug: ['#fffbeb', '#fff7ed', '#fefce8'],
    les: ['#fdf2f8', '#fff1f2', '#fdf4ff'],
};
const ACCENT = { classic: '#f97316', doug: '#d97706', les: '#db2777' };

function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

function fitText(ctx, text, maxWidth, size, weight = 700) {
    let s = size;
    ctx.font = `${weight} ${s}px ${SANS}`;
    while (ctx.measureText(text).width > maxWidth && s > 20) {
        s -= 2;
        ctx.font = `${weight} ${s}px ${SANS}`;
    }
    return s;
}

/** Draw the recap on `canvas` (resized to 1080×1350). `themeKey` is 'classic' | 'doug' | 'les'. */
export function drawRecap(canvas, recap, themeKey = 'classic') {
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    const [c1, c2, c3] = RECAP_GRADIENTS[themeKey] || RECAP_GRADIENTS.classic;
    const accent = ACCENT[themeKey] || ACCENT.classic;

    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, c1); g.addColorStop(0.5, c2); g.addColorStop(1, c3);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    // Card
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.08)';
    ctx.shadowBlur = 40;
    ctx.shadowOffsetY = 12;
    ctx.fillStyle = 'rgba(255,255,255,0.94)';
    roundRect(ctx, 60, 60, W - 120, H - 120, 56);
    ctx.fill();
    ctx.restore();

    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#1f2937';
    ctx.font = `700 34px ${SANS}`;
    ctx.fillText('💖 Our Restaurant Journey 💖', W / 2, 150);
    ctx.fillStyle = accent;
    ctx.font = `800 76px ${SANS}`;
    ctx.fillText(recap.year === 'all' ? 'All time' : `Our ${recap.year}`, W / 2, 245);
    ctx.fillStyle = '#6b7280';
    ctx.font = `500 30px ${SANS}`;
    ctx.fillText('in restaurants', W / 2, 290);

    // Big numbers
    const stats = [
        [String(recap.placesTried), 'places tried'],
        [String(recap.nightsOut), 'nights out'],
        [recap.checks ? `$${Math.round(recap.totalSpent).toLocaleString('en-US')}` : '—', 'on checks'],
    ];
    const colW = (W - 200) / 3;
    stats.forEach(([value, label], i) => {
        const x = 100 + colW * i + colW / 2;
        ctx.fillStyle = '#1f2937';
        fitText(ctx, value, colW - 20, 84, 800);
        ctx.fillText(value, x, 420);
        ctx.fillStyle = '#6b7280';
        ctx.font = `500 28px ${SANS}`;
        ctx.fillText(label, x, 462);
    });

    // Rows
    let y = 560;
    const row = (emoji, title, detail) => {
        ctx.textAlign = 'left';
        ctx.font = `64px ${EMOJI_FONT}`;
        ctx.fillText(emoji, 130, y + 14);
        ctx.fillStyle = '#6b7280';
        ctx.font = `600 26px ${SANS}`;
        ctx.fillText(title.toUpperCase(), 230, y - 22);
        ctx.fillStyle = '#1f2937';
        fitText(ctx, detail, W - 360, 40, 700);
        ctx.fillText(detail, 230, y + 24);
        ctx.textAlign = 'center';
        y += 150;
    };

    if (recap.favoritePlace) row('⭐', 'Our favorite', `${recap.favoritePlace.name} · ${recap.favoritePlace.rating}/10`);
    if (recap.bestDateNight) row('🥂', 'Best date night', `${recap.bestDateNight.name} · ${formatLocalDate(recap.bestDateNight.date, { month: 'short', day: 'numeric' })} · 💛 ${recap.bestDateNight.doug} 💖 ${recap.bestDateNight.les}`);
    if (recap.checkBattle.winner) {
        const cb = recap.checkBattle;
        const who = cb.winner === 'doug' ? '💛 Doug' : cb.winner === 'les' ? '💖 Les' : '🤝 A tie';
        row('🏆', 'Check Battle champ', `${who} · ${cb.doug}–${cb.les}${cb.ties ? ` (${cb.ties} tie${cb.ties === 1 ? '' : 's'})` : ''}`);
    }
    if (recap.topCuisine) row('🍽️', 'Our go-to', cuisineLabel(recap.topCuisine.id));
    if (recap.closestGuess && y < 1100) row('🎯', 'Closest guess', `${recap.closestGuess.who === 'doug' ? 'Doug' : 'Les'} · $${recap.closestGuess.diff.toFixed(2)} off at ${recap.closestGuess.name}`);

    ctx.fillStyle = '#9ca3af';
    ctx.font = `500 26px ${SANS}`;
    ctx.textAlign = 'center';
    ctx.fillText('Made with love', W / 2, H - 110);
    return canvas;
}

/** Render and share (or download) the recap image. Resolves to 'shared' | 'downloaded' | 'cancelled'. */
export async function shareRecap(recap, themeKey) {
    const canvas = drawRecap(document.createElement('canvas'), recap, themeKey);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    const name = `our-${recap.year === 'all' ? 'restaurants' : recap.year}-in-restaurants.png`;
    const file = new File([blob], name, { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
            await navigator.share({ files: [file], title: 'Our Year in Restaurants' });
            return 'shared';
        } catch (e) {
            if (e && e.name === 'AbortError') return 'cancelled';
        }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    return 'downloaded';
}
